import { ChevronDownward } from 'easyship-components/icons';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { twMerge } from 'tailwind-merge';
import { createPortal } from 'react-dom';
import { ArrowTooltip } from 'easyship-components';
import { CategoryDropDown } from './CategoryDropDown';
import { useCategoryHSCodeContext } from './context/context';
import {
  CategoryHSCodeState,
  PortalVerticalDirection,
  CategoryHSCodeSelectionProps,
} from './types';
import CategoryHSCodeSearch from './CategoryHSCodeSearch';

export function CategoryHSCodeComponent({
  className,
  classNameDropdown,
  classNameSearch,
  classNamePortal,
  status,
}: Omit<CategoryHSCodeSelectionProps, 'onChange' | 'value'>) {
  const {
    open,
    toggleOpen,
    setRef,
    selectedValue,
    portalPosition,
    componentState,
    setPortalRef,
    closeDropDown,
    openDropDown,
  } = useCategoryHSCodeContext();

  const isDropDown = componentState === CategoryHSCodeState.DROPDOWN;

  const handleKeyDownButton = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeDropDown();
      return;
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.stopPropagation();
      e.preventDefault();
      openDropDown();
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={toggleOpen}
        className={twMerge(
          `px-2 py-2 text-base text-ink-700 focus:outline-none border border-transparent focus:border
          focus:border-ink-100 flex items-center justify-between gap-2 relative cursor-pointer rounded-sm
          min-w-[180px] w-full  outline-none`,
          status === 'error' && 'bg-red-100 border border-red-700 focus:border-red-700',
          className
        )}
        onKeyDown={handleKeyDownButton}
        ref={setRef}
      >
        {selectedValue?.label ? (
          <ArrowTooltip
            tooltipClassName="delay-500"
            label={<span>{selectedValue?.label}</span>}
            className={twMerge('w-full overflow-hidden text-left text-ellipsis whitespace-nowrap')}
          >
            <span className="w-full whitespace-nowrap overflow-hidden text-ellipsis">
              {selectedValue?.label}
            </span>
          </ArrowTooltip>
        ) : (
          <span className="text-base text-ink-300 opacity-50">
            <FormattedMessage id="global.choose-one" />
          </span>
        )}
        <ChevronDownward
          className={twMerge(
            'min-h-5 h-5 min-w-5 w-5 ml-2 transition-transform text-ink-500',
            open && 'rotate-180'
          )}
        />
      </button>

      {createPortal(
        <div
          ref={setPortalRef}
          className={twMerge(
            'fixed z-[3000] bg-white border border-sky-300 shadow-md rounded-sm mt-1 w-fit overflow-hidden',
            classNamePortal,
            open ? 'max-h-[380px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none',
            portalPosition.direction === PortalVerticalDirection.UP ? 'origin-bottom' : 'origin-top'
          )}
          style={{
            top: portalPosition.top,
            bottom: portalPosition.bottom,
            left: portalPosition.left,
          }}
          role="listbox"
          aria-expanded="true"
          data-role="category-hs-code-selection-portal"
          data-open={open ? 'true' : 'false'}
        >
          {isDropDown ? (
            <CategoryDropDown
              className={twMerge(
                classNameDropdown,
                portalPosition.direction === PortalVerticalDirection.UP && 'flex-col-reverse'
              )}
            />
          ) : (
            <CategoryHSCodeSearch
              className={twMerge(
                classNameSearch,
                portalPosition.direction === PortalVerticalDirection.UP && 'flex-col-reverse'
              )}
            />
          )}
        </div>,
        document.body
      )}
    </>
  );
}

export default CategoryHSCodeComponent;
